import 'rxjs'

import * as constants from '../constants'
import { Error as SocketError, Login, Prices, ServerType } from '@bdswiss/mt4-connector'
import { REFRESH_ACCOUNT_VALUES, REFRESH_TOTAL_PL } from '../constants'
import { Action } from 'redux'
import {
  CloseTradeAction,
  FulfillCloseTradeAction,
  FulfillModifyTradeAction,
  FulfillTickAction,
  FulfillOpenTradeAction,
  ModifyTradeAction,
  OpenTradeAction,
  SetAmount,
  SetTakeProfit,
  SetStopLoss,
  InputPositionSL,
  InputPositionTP,
  SetPrice,
  InputPositionPrice,
  FulfillPendingTradeAction,
  FulfillActivatedTradeAction,
  CancelPendingOrderAction,
  FulfillCancelTradeAction,
  FulfillModifyPendingTradeAction,
} from './TradeActions'
import { AccountDetails, Trades, ViewerWrapper, AppType } from '../types'
import { Observable } from 'rxjs/Observable'
import { getServerTypeByAccountType } from '../utils/common-utils'
import { SetLocale } from './commonActions'

export interface DoLogin extends Action {
  type: constants.DO_LOGIN
  login: string
  token?: string
  server: ServerType
}

export function doLogin({ login, token, server }: Login): DoLogin {
  return {
    type: constants.DO_LOGIN,
    login,
    token,
    server,
  }
}

export interface TogglePositions {
  type: constants.TOGGLE_POSITIONS
}

export function togglePositions(): TogglePositions {
  return {
    type: constants.TOGGLE_POSITIONS,
  }
}

export interface ToggleAssets {
  type: constants.TOGGLE_ASSETS
}

export function toggleAssets(): ToggleAssets {
  return {
    type: constants.TOGGLE_ASSETS,
  }
}

export interface SetAssetsGroup {
  type: constants.SET_ASSETS_GROUP
  group: string
}

export function setAssetsGroup(group: string): SetAssetsGroup {
  return {
    type: constants.SET_ASSETS_GROUP,
    group: group,
  }
}

export interface ToggleTradingPane {
  type: constants.TOGGLE_TRADING_PANE
}

export function toggleTradingPane(): ToggleTradingPane {
  return {
    type: constants.TOGGLE_TRADING_PANE,
  }
}

export interface OpenEditPane {
  type: constants.OPEN_EDIT_PANE
}

export function openEditPane(): OpenEditPane {
  return {
    type: constants.OPEN_EDIT_PANE,
  }
}

export interface CloseAllTrades {
  type: constants.CLOSE_ALL_TRADES
}

export function closeAllTrades(): CloseAllTrades {
  return {
    type: constants.CLOSE_ALL_TRADES,
  }
}

export interface SetDeposit {
  type: constants.SET_DEPOSIT
  // deposit: number;
}

export function setDeposit(): SetDeposit {
  return {
    type: constants.SET_DEPOSIT,
    // deposit
  }
}

export interface ChangeSymbol {
  type: constants.CHANGE_SYMBOL
  symbol: string
}

export function changeSymbol(symbol: string): ChangeSymbol {
  return {
    type: constants.CHANGE_SYMBOL,
    symbol,
  }
}

export interface ShowSuccessMessage {
  type: constants.SHOW_SUCCESS_MESSAGE
  message: string
  header?: string
}

export function showSuccess(message: string, header?: string): ShowSuccessMessage {
  return {
    type: constants.SHOW_SUCCESS_MESSAGE,
    message,
    header,
  }
}

export interface ShowErrorMessage {
  type: constants.SHOW_ERROR_MESSAGE
  error: Error
}

export function showError(error: Error): ShowErrorMessage {
  return {
    type: constants.SHOW_ERROR_MESSAGE,
    error,
  }
}

export function errorObservable(error: Error): Observable<ShowErrorMessage> {
  const errorAction: ShowErrorMessage = {
    type: constants.SHOW_ERROR_MESSAGE,
    error,
  }
  return Observable.of(errorAction)
}

export interface ShowSocketErrorMessage {
  type: constants.SHOW_SOKET_ERROR_MESSAGE
  error: SocketError
}

export function showSocketError(error: SocketError): ShowSocketErrorMessage {
  return {
    type: constants.SHOW_SOKET_ERROR_MESSAGE,
    error,
  }
}

export interface SetType {
  type: constants.SET_ACCOUNT_TYPE
  appType: AppType
}

export function setType(typeFullName: string): SetType {
  const appType = typeSelectorValue(typeFullName)
  return {
    type: constants.SET_ACCOUNT_TYPE,
    appType,
  }
}

export interface SetAccount {
  type: constants.SET_ACCOUNT
  account: AccountDetails
}

export function setAccount(userAccount: ViewerWrapper, preferredAccountType: string): SetAccount {
  const account = findAccountWithGreatestBalance(userAccount, preferredAccountType)
  return {
    type: constants.SET_ACCOUNT,
    account,
  }
}

const typeSelectorValue = (typeFullName: string) => {
  let out: AppType
  switch (true) {
    case typeFullName.toLowerCase().indexOf('forex') > -1:
      out = 'forex'
      break
    case typeFullName.toLowerCase().indexOf('bond') > -1:
      out = 'bond'
      break
    default:
      out = 'forex'
  }
  return out
}

function findAccountWithGreatestBalance(userAccount: ViewerWrapper, preferredAccountType: string) {
  const accounts = [...userAccount.viewer.accounts]
    .map((acc) => {
      return {
        ...acc,
        serverType: getServerTypeByAccountType(acc.__typename),
        suitableType: acc.__typename.toLowerCase().indexOf(preferredAccountType) > -1,
      }
    })
    .sort((a, b) => {
      if (a.suitableType !== b.suitableType) {
        return a.suitableType ? -1 : 1
      } else if (a.serverType === b.serverType) {
        return b.balance - a.balance
      } else if (a.serverType === 'demo') {
        return 1
      } else {
        return -1
      }
    })
  return accounts[0] as AccountDetails
}

export interface SelectAccount {
  type: constants.SELECT_ACCOUNT
  accountId: string
  id?: number
}

export function selectAccount(accountId: string, id?: number): SelectAccount {
  return {
    type: constants.SELECT_ACCOUNT,
    accountId,
    id,
  }
}

export interface SelectPosition {
  type: constants.SELECT_POSITION
  order: number
  expanded?: boolean
}

export function selectPosition(order: number, expanded?: boolean): SelectPosition {
  return {
    type: constants.SELECT_POSITION,
    order,
    expanded,
  }
}

export interface SelectOrder {
  type: constants.SELECT_ORDER
  order: number
  expanded?: boolean
}

export function selectOrder(order: number, expanded?: boolean): SelectOrder {
  return {
    type: constants.SELECT_ORDER,
    order,
    expanded,
  }
}

export interface RefreshTotalPL {
  type: REFRESH_TOTAL_PL
  totalPL: number
}

export function refreshTotalPL(totalPL: number): RefreshTotalPL {
  return {
    type: REFRESH_TOTAL_PL,
    totalPL,
  }
}

export interface RefreshAccountValues {
  type: REFRESH_ACCOUNT_VALUES
  freeMargin: number
  equity: number
  balance?: number
  margin?: number
}

export function refreshAccountValues(
  freeMargin: number,
  equity: number,
  balance?: number,
  margin?: number
): RefreshAccountValues {
  return {
    type: REFRESH_ACCOUNT_VALUES,
    freeMargin,
    equity,
    balance,
    margin,
  }
}

export interface RefreshTrades extends Action {
  type: constants.REFRESH_TRADES
  trades: Trades
}

export function refreshTrades(trades: Trades): RefreshTrades {
  return {
    type: constants.REFRESH_TRADES,
    trades,
  }
}

export interface ResubscribeSymbols extends Action {
  type: constants.RESUBSCRIBED_SYMBOLS
  payload: Set<string>
}

export function resubscribeSymbols(payload: Set<string>): ResubscribeSymbols {
  return {
    type: constants.RESUBSCRIBED_SYMBOLS,
    payload,
  }
}

export interface RefreshSubscribedSymbols extends Action {
  type: constants.REFRESH_SUBSCRIBED_SYMBOLS
  payload: Prices
}

export function refreshSubscribedSymbols(subscribedSymbols: Prices): RefreshSubscribedSymbols {
  return {
    type: constants.REFRESH_SUBSCRIBED_SYMBOLS,
    payload: subscribedSymbols,
  }
}

export interface ConnectionReestablished extends Action {
  type: constants.CONNECTION_REESTABLISHED
}

export function connectionReestablished(): ConnectionReestablished {
  return {
    type: constants.CONNECTION_REESTABLISHED,
  }
}

export type TradingAction =
  | FulfillOpenTradeAction
  | FulfillPendingTradeAction
  | FulfillActivatedTradeAction
  | FulfillCancelTradeAction
  | FulfillModifyPendingTradeAction
  | FulfillCloseTradeAction
  | FulfillModifyTradeAction
  | OpenTradeAction
  | SetAmount
  | SetTakeProfit
  | SetStopLoss
  | SetPrice
  | SetLocale
  | InputPositionSL
  | InputPositionTP
  | InputPositionPrice
  | CancelPendingOrderAction
  | CloseTradeAction
  | CloseAllTrades
  | TogglePositions
  | ToggleAssets
  | ToggleTradingPane
  | OpenEditPane
  | ModifyTradeAction
  | SetDeposit
  | ChangeSymbol
  // Messages action
  | ShowSuccessMessage
  | ShowErrorMessage
  | ShowSocketErrorMessage
  | SelectAccount
  | SelectPosition
  | SelectOrder
  | FulfillTickAction
  // Calculation actions
  | RefreshTotalPL
  | RefreshAccountValues
  | RefreshTrades
